import { AxiosInstance } from 'axios';
import { apiBaseUrl } from './baseUrl';
import { ExactLimitsBoard } from './exactLimitsBoard';

/** The kinds of signal a watch counts; mirrors RolloutSignalKind. */
export type SignalKind =
    | 'Arrived'
    | 'Succeeded'
    | 'Failed'
    | 'Postponed'
    | 'RateLimited'
    | 'TokenRefreshed'
    | 'TokenFailed';

export type Counts = Partial<Record<SignalKind, number>>;

/** One watch as stored; mirrors RolloutWatchEntity. */
export interface RolloutWatchSummary {
    rowKey: string;
    moneyCheck?: boolean;
    reevaluationState?: string | null;
    customer: string;
    environment: string;
    deployedAt: string;
    status: 'watching' | 'done' | 'stopped';
    /** 'monitor' for an environment followed without a deploy; absent for a deploy watch. */
    kind?: string | null;
    startedBy?: string | null;
    buildBefore?: string | null;
    buildAfter?: string | null;
}

export interface RolloutMinute {
    minute: string;
    counts: Counts;
}

/** One group of failures in a watch; mirrors RolloutProblem. */
export interface RolloutProblem {
    code: string;
    title: string;
    owner?: string | null;
    requeueHelps: boolean;
    before: number;
    after: number;
    isNew: boolean;
    firstAt?: string | null;
    lastAt?: string | null;
    objects: number;
    healed: number;
    examples: string[];
    /** Objects failed after the deploy that had failed before it already (old backlog offered again). */
    backlog?: number;
    /** Objects failed after the deploy that never failed before it. */
    fresh?: number;
    /** For payments whose invoice was not in Exact: each invoice and how the connector has it now. */
    waitingOn?: string[] | null;
    /** How many of those invoices the connector gave up on; then the group is ours. */
    stuck?: number;
}

/** The totals of one period per object, not per attempt; mirrors RolloutCounts. */
export interface RolloutCounts {
    offered: number;
    arrived: number;
    reoffered: number;
    succeeded: number;
    failed: number;
    open: number;
    failedAttempts: number;
}

/** A watch in full; mirrors RolloutWatchView. */
export interface RolloutWatchView {
    id: string;
    customer: string;
    environment: string;
    deployedAt: string;
    endsAt: string;
    status: RolloutWatchSummary['status'];
    kind?: string | null;
    startedBy?: string | null;
    buildBefore?: string | null;
    buildAfter?: string | null;
    readAt?: string | null;
    error?: string | null;
    timeline: RolloutMinute[];
    throughput: RolloutMinute[];
    before: Counts;
    after: Counts;
    /** Per object: the day before the deploy. */
    beforeObjects?: RolloutCounts | null;
    /** Per object: since the deploy. */
    afterObjects?: RolloutCounts | null;
    problems: RolloutProblem[];
    healed: number;
    exactLimits?: ExactLimitsBoard | null;
    scanner?: ScannerComparison | null;
    lights: ReadinessCheck[];
    rollback: RollbackInfo;
    reevaluationRunId?: string | null;
    reevaluationState?: string | null;
    telemetry: TelemetryProblem[];
    telemetryAvailable?: boolean | null;
    /** Short label; what App Insights said is in telemetryUnavailableDetail. */
    telemetryUnavailable?: string | null;
    telemetryUnavailableDetail?: string | null;
    canaries: RolloutCanary[];
    webhooks: WebhookSummary;
    money: MoneySummary;
    apps: RolloutAppState[];
    /** Newest first. */
    events: RolloutEvent[];
    freshness: RolloutFreshness;
}

/** One function app now, from its heartbeats; mirrors RolloutAppState. */
export interface RolloutAppState {
    app: string;
    running: number;
    startedAt?: string | null;
    lastBeat?: string | null;
    build?: string | null;
}

export type RolloutEventKind =
    | 'Started'
    | 'Restarted'
    | 'Stopped'
    | 'Silent'
    | 'Deploy'
    | 'Problem'
    | 'Telemetry'
    | 'Light'
    | 'LightCleared';

/** One moment in a watch; mirrors RolloutEvent. */
export interface RolloutEvent {
    at: string;
    kind: RolloutEventKind;
    subject: string;
    text: string;
}

/** When each source was read and how often (seconds); mirrors RolloutFreshness. */
export interface RolloutFreshness {
    /** False once the watch is over: it is read no more and every interval is 0. */
    live?: boolean;
    tables?: string | null;
    tablesEvery: number;
    objects?: string | null;
    objectsEvery: number;
    apps?: string | null;
    appsEvery: number;
    telemetry?: string | null;
    telemetryEvery: number;
    telemetryUpTo?: string | null;
    telemetryIngestedAt?: string | null;
    context?: string | null;
    contextEvery: number;
    webhooks?: string | null;
    webhooksEvery: number;
    sample?: string | null;
    sampleEvery: number;
    scanner?: string | null;
    scannerEvery: number;
}

/** Webhooks Chargebee could not deliver in the window; mirrors WebhookSummary. */
export interface WebhookSummary {
    checkedAt?: string | null;
    error?: string | null;
    failed: number;
    rescheduled: number;
    stuck: number;
    examples: {
        eventId: string;
        eventType: string;
        occurredAt: string;
        status: string;
    }[];
}

/** The first object of a type coupled after the deploy, checked in Exact; mirrors RolloutCanary. */
export interface RolloutCanary {
    objectType: string;
    relationId: string;
    coupledAt: string;
    state: ReadinessState;
    /** Short label of what Exact said. */
    detail: string;
    /** Why Exact was not asked, for a tooltip. */
    reason?: string | null;
}

/** One group of errors in Application Insights; mirrors TelemetryProblem. */
export interface TelemetryProblem {
    code: string;
    kind: 'exception' | 'trace';
    pattern: string;
    role?: string | null;
    operation?: string | null;
    before: number;
    after: number;
    isNew: boolean;
    first?: string | null;
    last?: string | null;
    /** One message as it was logged; the pattern hides names and values. */
    sample?: string | null;
    /** How many lines came from a run in which a document waited for its customer. */
    expected: number;
    /** Why no one needs to act, when every line that matters was expected (after the deploy, or before it). */
    expectedReason?: string | null;
    /** How many lines after the deploy were expected. */
    expectedAfter: number;
}

/** The watch as a Markdown report, saved as a file. */
export const downloadRolloutReport = async (
    axios: AxiosInstance,
    view: RolloutWatchView
): Promise<void> => {
    const response = await axios.get('/Rollout/report', {
        params: { id: view.id },
        responseType: 'text',
    });
    const url = URL.createObjectURL(
        new Blob([response.data], { type: 'text/markdown' })
    );
    const link = document.createElement('a');

    link.href = url;
    link.download = `uitrol-${view.customer}-${
        view.environment
    }-${view.deployedAt.slice(0, 16).replace(/[-:T]/g, '')}.md`;
    link.click();
    URL.revokeObjectURL(url);
};

export const reevaluateRolloutWatch = async (
    axios: AxiosInstance,
    id: string
): Promise<void> => {
    await axios.post('/Rollout/reevaluate', null, { params: { id } });
};

export interface ScannerDifference {
    object: string;
    liveCode: string;
    sweepCode: string;
    sweepStatus?: string | null;
}

/** The watch held against the backlog; mirrors ScannerComparison. */
export interface ScannerComparison {
    sweptAt?: string | null;
    agree: number;
    missing: string[];
    reclassified: ScannerDifference[];
    healedButOpen: string[];
    resolvedSinceDeploy: number;
}

export interface RollbackInfo {
    rollbackRun?: string | null;
    signedOffBy?: string | null;
    /** Short label; the full step is in afterRollbackDetail. */
    afterRollback?: string | null;
    afterRollbackDetail?: string | null;
}

export const rolloutHubUrl = `${apiBaseUrl.replace(/\/+$/, '')}/hubs/rollout`;

export const ROLLOUT_WATCHES_METHOD = 'rolloutWatches';
export const ROLLOUT_WATCH_METHOD = 'rolloutWatch';
export const ROLLOUT_PROBLEM_METHOD = 'rolloutProblem';

// The shared axios instance hands JSON over as text, as the generated clients expect.
const body = <T>(data: unknown): T =>
    (typeof data === 'string' && data.length > 0
        ? JSON.parse(data)
        : data) as T;

export const getRolloutWatches = async (
    axios: AxiosInstance
): Promise<RolloutWatchSummary[]> =>
    body<RolloutWatchSummary[]>((await axios.get('/Rollout')).data);

export const getRolloutWatch = async (
    axios: AxiosInstance,
    id: string
): Promise<RolloutWatchView> =>
    body<RolloutWatchView>(
        (await axios.get('/Rollout/watch', { params: { id } })).data
    );

export const startRolloutWatch = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    deployedAt?: string
): Promise<RolloutWatchSummary> =>
    body<RolloutWatchSummary>(
        (
            await axios.post(
                `/Rollout/${encodeURIComponent(customer)}/${encodeURIComponent(
                    environment
                )}/start`,
                null,
                { params: deployedAt ? { deployedAt } : undefined }
            )
        ).data
    );

export type ReadinessState = 'Ok' | 'Warn' | 'Bad' | 'Unknown';

/** One check or rollback light; mirrors ReadinessCheck. */
export interface ReadinessCheck {
    key: string;
    /** Reads as the state: a red light names the situation ("Tokenlus"), others the good outcome. */
    label: string;
    state: ReadinessState;
    detail: string;
}

export interface DeployCard {
    receivables?: boolean | null;
    watermarkProcessedUpTo?: string | null;
    watermarkAdvancedAt?: string | null;
    featuresOn: string[];
    settingNames: string[];
    builds: string[];
    group: string;
    runningBuildNumber?: string | null;
    runningCommit?: string | null;
}

export interface RolloutPlan {
    rollbackRun?: string | null;
    note?: string | null;
    signedOffBy?: string | null;
    signedOffAt?: string | null;
}

/** The go/no-go and deploy card of one environment; mirrors RolloutReadinessView. */
export interface RolloutReadiness {
    customer: string;
    environment: string;
    checkedAt: string;
    verdict: ReadinessState;
    checks: ReadinessCheck[];
    card: DeployCard;
    plan?: RolloutPlan | null;
}

export const getRolloutReadiness = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<RolloutReadiness> =>
    body<RolloutReadiness>(
        (
            await axios.get(
                `/Rollout/${encodeURIComponent(customer)}/${encodeURIComponent(
                    environment
                )}/readiness`
            )
        ).data
    );

export const saveRolloutPlan = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    plan: { rollbackRun?: string; note?: string; signOff: boolean }
): Promise<void> => {
    await axios.put(
        `/Rollout/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/plan`,
        plan,
        {
            headers: { 'Content-Type': 'application/json' },
        }
    );
};

/** One environment on the overview; mirrors RolloutEnvironmentRow. */
export interface RolloutEnvironmentRow {
    customer: string;
    environment: string;
    stage: 'prod' | 'test';
    builds?: string | null;
    watchId?: string | null;
    watchStatus?: RolloutWatchSummary['status'] | null;
    deployedAt?: string | null;
    newProblems: number;
    failedAfter: number;
    healed: number;
    worst?: ReadinessState | null;
    exactDailyRemaining?: number | null;
    exactDailyLimit?: number | null;
    /** The pipeline's build number per app (`app=20261030.2;...`), as last read. */
    buildNumbers?: string | null;
}

export const getRolloutOverview = async (
    axios: AxiosInstance
): Promise<RolloutEnvironmentRow[]> =>
    body<RolloutEnvironmentRow[]>((await axios.get('/Rollout/overview')).data);

export const getEnvironmentWatches = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<RolloutWatchSummary[]> =>
    body<RolloutWatchSummary[]>(
        (
            await axios.get(
                `/Rollout/${encodeURIComponent(customer)}/${encodeURIComponent(
                    environment
                )}/watches`
            )
        ).data
    );

export const setMoneyCheck = async (
    axios: AxiosInstance,
    id: string,
    on: boolean
): Promise<void> => {
    await axios.post('/Rollout/money', null, { params: { id, on } });
};

/** The money check of a watch; mirrors MoneySummary. */
export interface MoneySummary {
    enabled: boolean;
    checkedAt?: string | null;
    checked: number;
    equal: number;
    skipped: number;
    differences: {
        objectType: string;
        relationId: string;
        chargebee?: number | null;
        exact?: number | null;
        reason: string;
    }[];
}

export const stopRolloutWatch = async (
    axios: AxiosInstance,
    id: string
): Promise<void> => {
    await axios.post('/Rollout/stop', null, { params: { id } });
};

/** Who does a step of the rollout checklist; mirrors ChecklistKind. */
export type ChecklistKind = 'Auto' | 'Mcp' | 'Manual';

/** Where a step stands; mirrors ChecklistState. */
export type ChecklistState =
    | 'Done'
    | 'Open'
    | 'Warn'
    | 'Bad'
    | 'Waiting'
    | 'Unknown';

export interface ChecklistTick {
    note?: string | null;
    by?: string | null;
    at: string;
}

/** One step; mirrors ChecklistStepView. */
export interface ChecklistStep {
    key: string;
    phase: 'before' | 'deploy' | 'after' | 'followup';
    title: string;
    kind: ChecklistKind;
    state: ChecklistState;
    detail?: string | null;
    how?: string | null;
    ticket?: string | null;
    tick?: ChecklistTick | null;
}

/** One deploy run with its steps; mirrors ChecklistDeployView. */
export interface ChecklistDeploy {
    id: string;
    title: string;
    prefix?: string | null;
    stage?: string | null;
    branch: string;
    environments: string[];
    notBefore?: string | null;
    why?: string | null;
    tickets: string[];
    status: 'open' | 'wacht' | 'bezig' | 'klaar';
    done: number;
    total: number;
    next?: string | null;
    checkedAt?: string | null;
    steps: ChecklistStep[];
}

/** The whole rollout; mirrors ChecklistOverview. */
export interface ChecklistOverview {
    planId: string;
    title: string;
    branch: string;
    next?: string | null;
    deploys: ChecklistDeploy[];
}

export const ROLLOUT_CHECKLIST_METHOD = 'rolloutChecklist';
export const ROLLOUT_CHECKLIST_OVERVIEW_METHOD = 'rolloutChecklistOverview';

export const getRolloutChecklist = async (
    axios: AxiosInstance
): Promise<ChecklistOverview> =>
    body<ChecklistOverview>((await axios.get('/Rollout/checklist')).data);

export const tickChecklistStep = async (
    axios: AxiosInstance,
    deploy: string,
    step: string,
    done: boolean,
    note?: string
): Promise<ChecklistDeploy> =>
    body<ChecklistDeploy>(
        (
            await axios.put(
                `/Rollout/checklist/${encodeURIComponent(
                    deploy
                )}/${encodeURIComponent(step)}`,
                { done, note },
                { headers: { 'Content-Type': 'application/json' } }
            )
        ).data
    );
